import moment from 'moment'

function formatTime(value, datetimeFormat) {
  if (!value)
    return '—'
  const m = moment(value)
  return m.isValid() ? m.format(datetimeFormat || 'MM/DD/YYYY hh:mm A') : String(value)
}

function formatMileage(value) {
  if (value == null || value === '')
    return '—'
  return String(value)
}

function bookingStatusMessage(booking) {
  if (!booking)
    return ''
  return String(booking._status_msg_base || booking.status_msg || '').trim()
}

export function buildBookingDetailCm(booking, datetimeFormat, resourceType) {
  const actual = (booking && booking.actual) || {}
  const isVehicle = resourceType === 'vehicle'
  return {
    key: (booking && (booking.bookingID || booking.charterID)) || Math.random(),
    charter_id: booking && booking.charterID,
    driver: booking && booking.driver,
    vehicle_id: booking && booking.vehicle,
    planned_start: formatTime(booking && booking.start_datetime, datetimeFormat),
    planned_finish: formatTime(booking && booking.finish_datetime, datetimeFormat),
    actual_start: formatTime(
      isVehicle ? actual.nearestLocationStartDateTime : actual.nearestLoginStartDateTime,
      datetimeFormat
    ),
    actual_finish: formatTime(
      isVehicle ? actual.nearestLocationFinishDateTime : actual.nearestLogoutFinishDateTime,
      datetimeFormat
    ),
    planned_mileage: formatMileage(booking && booking.plannedDistance),
    actual_mileage: formatMileage(actual.actualTotalDistance),
    status: booking && booking.status,
    status_msg: bookingStatusMessage(booking),
  }
}

export function buildBookingDetailTbn(booking, datetimeFormat, resourceType) {
  const actual = (booking && booking.actual) || {}
  const isVehicle = resourceType === 'vehicle'
  return {
    key: (booking && booking.bookingID) || Math.random(),
    charter_id: booking && booking.bookingID,
    driver: booking && booking.driver,
    vehicle_id: booking && booking.vehicle,
    planned_start: formatTime(booking && booking.start_datetime, datetimeFormat),
    planned_finish: formatTime(booking && booking.finish_datetime, datetimeFormat),
    actual_start: formatTime(
      isVehicle ? actual.actualStartLocationTime : actual.actualClockInTime,
      datetimeFormat
    ),
    actual_finish: formatTime(
      isVehicle ? actual.actualFinishLocationTime : actual.actualClockOutTime,
      datetimeFormat
    ),
    planned_mileage: '—',
    actual_mileage: formatMileage(actual.actualTotalDistance),
    status: booking && booking.status,
    status_msg: bookingStatusMessage(booking),
  }
}

export function buildServiceEventDetail(event, datetimeFormat) {
  return {
    key: (event && event.bookingID) || Math.random(),
    is_service_event: true,
    event_type: (event && event.event_type) || 'Maintenance',
    comment: event && event.comment,
    planned_start: formatTime(event && event.start_datetime, datetimeFormat),
    planned_finish: formatTime(event && event.finish_datetime, datetimeFormat),
  }
}

export function buildBookingDetailsFromSegments(segments, variant, datetimeFormat, resourceType, plannedOnly, actualsPath) {
  const build = variant === 'tbn' ? buildBookingDetailTbn : buildBookingDetailCm
  const seen = new Set()
  const details = []
  for (const seg of segments) {
    if (!seg || !seg.booking)
      continue
    const key = seg.booking.bookingID || seg.booking.charterID || seg.booking_id
    if (seen.has(key))
      continue
    seen.add(key)
    if (seg.booking.is_service_event) {
      details.push(buildServiceEventDetail(seg.booking, datetimeFormat))
      continue
    }
    const detail = build(seg.booking, datetimeFormat, resourceType)
    if (plannedOnly) {
      // planned-schedule views (VSM Upcoming) never have actuals — drop the
      // rows instead of showing em-dashes; the panel hides null fields.
      // mileage adds nothing to a schedule view, so both variants go too
      detail.actual_start = null
      detail.actual_finish = null
      detail.actual_mileage = null
      detail.planned_mileage = null
    }
    if (actualsPath) {
      // direct link to this booking on the actuals page; the TBN hash pins
      // the exact record, bookingID is the CM (and TBN fallback) search key
      const bookingRef = seg.booking.tbn_booking_hash || seg.booking.bookingID || seg.booking.charterID
      if (bookingRef)
        detail.actuals_href = './#' + actualsPath + '?booking_id=' + encodeURIComponent(bookingRef)
    }
    details.push(detail)
  }
  return details
}

// the click-modal's "complete details" view: the standard summary plus the
// field set the actuals pages' tables carry, rendered as generic label/value
// rows. records are the actuals web records (the timeline pages already hold
// them; VSM Upcoming fetches them by booking ref on click).
function buildBookingDetailFull(record, variant, datetimeFormat, resourceType) {
  const build = variant === 'tbn' ? buildBookingDetailTbn : buildBookingDetailCm
  const detail = build(record, datetimeFormat, resourceType)
  const rows = []
  const add = (label, value) => {
    if (value != null && value !== '' && value !== '—')
      rows.push({ label, value })
  }
  const addTime = (label, value) => {
    if (value)
      add(label, formatTime(value, datetimeFormat))
  }
  if (variant === 'tbn') {
    add('Route', record.routeDescription)
    add('Vehicle Type', record.vehicle_type)
    add('Start Base', record.startBase)
    add('Finish Base', record.finishBase)
    if (record.day_of_trip)
      add('Trip Day', record.final_day_of_trip ? record.day_of_trip + ' of ' + record.final_day_of_trip : record.day_of_trip)
    addTime('Driver Start', record.driver_start_date)
    addTime('Driver Finish', record.driver_finish_date)
    addTime('Booking Update', record.booking_updated)
    addTime('Actuals Processed', record.processed_time)
    addTime('Actuals Update', record.last_updated)
  } else {
    add('Movement ID', record.movementID)
    add('Vehicle Type', record.vehicle_type)
    add('Type', record.bookingType)
    add('Base', record.bookingBase)
    add('Start Base', record.startBase)
    add('Finish Base', record.finishBase)
    add('Pickup', record.pickup)
    add('Destination', record.destination)
    add('Company', record.companyID)
    add('Client', record.clientID)
    add('Route', record.routeName)
    addTime('Booking Update', record.last_updated)
    addTime('Actuals Processed', record.processed_time)
    addTime('Actuals Update', record.actuals_last_updated)
    add('Notes', record.notes)
  }
  // the error row already renders status_msg for error rows
  if (record.status !== 'error')
    add('Status Message', bookingStatusMessage(record) || null)
  detail.extra_rows = rows
  return detail
}

export function buildFullBookingDetails(records, variant, datetimeFormat, resourceType, actualsPath) {
  const seen = new Set()
  const details = []
  for (const record of records || []) {
    if (!record)
      continue
    const key = record.ID || record.bookingID || record.charterID
    if (seen.has(key))
      continue
    seen.add(key)
    if (record.is_service_event) {
      details.push(buildServiceEventDetail(record, datetimeFormat))
      continue
    }
    const detail = buildBookingDetailFull(record, variant, datetimeFormat, resourceType)
    if (actualsPath) {
      const bookingRef = record.tbn_booking_hash || record.ID || record.bookingID || record.charterID
      if (bookingRef)
        detail.actuals_href = './#' + actualsPath + '?booking_id=' + encodeURIComponent(bookingRef)
    }
    details.push(detail)
  }
  return details
}

// anchor the panel centered under the hovered booking bar instead of the
// cursor: union the row's slice cells covered by the hovered segments, then
// center on that span just below the row. every cell of the same bar yields
// the same rect, so the panel holds still while the pointer sweeps the bar.
export function panelPositionUnderSegments(cellEl, segments, panelWidth, panelHeight) {
  const maxW = panelWidth || 360
  const maxH = panelHeight || 280
  const margin = 8
  const rowEl = cellEl && cellEl.closest ? cellEl.closest('tr') : null
  if (!rowEl || !segments || !segments.length)
    return null
  let spanStart = null
  let spanEnd = null
  for (const seg of segments) {
    const s = moment(seg.start_datetime)
    const e = moment(seg.end_datetime)
    if (!s.isValid() || !e.isValid())
      continue
    if (!spanStart || s.isBefore(spanStart)) spanStart = s
    if (!spanEnd || e.isAfter(spanEnd)) spanEnd = e
  }
  if (!spanStart || !spanEnd)
    return null
  let left = null
  let right = null
  let bottom = null
  let top = null
  for (const slice of rowEl.querySelectorAll('td.driver-actuals-util-slice[data-day]')) {
    const t = moment(
      `${slice.dataset.day} ${slice.dataset.hour}:${slice.dataset.minutes}`,
      'MM-DD-YYYY H:m'
    )
    if (!t.isValid())
      continue
    if (!t.isBefore(spanEnd) || !t.clone().add(15, 'minutes').isAfter(spanStart))
      continue
    const rect = slice.getBoundingClientRect()
    if (left == null || rect.left < left) left = rect.left
    if (right == null || rect.right > right) right = rect.right
    if (bottom == null || rect.bottom > bottom) bottom = rect.bottom
    if (top == null || rect.top < top) top = rect.top
  }
  if (left == null)
    return null
  let panelLeft = (left + right) / 2 - maxW / 2
  let panelTop = bottom + margin
  if (typeof window !== 'undefined') {
    if (panelLeft + maxW > window.innerWidth - margin)
      panelLeft = window.innerWidth - margin - maxW
    if (panelLeft < margin)
      panelLeft = margin
    // no room below the row: flip above it
    if (panelTop + maxH > window.innerHeight - margin)
      panelTop = Math.max(margin, top - maxH - margin)
  }
  return { top: Math.round(panelTop) + 'px', left: Math.round(panelLeft) + 'px' }
}

export function panelPositionFromEvent(event, panelWidth, panelHeight) {
  const margin = 12
  const maxW = panelWidth || 360
  const maxH = panelHeight || 280
  let left = event.clientX + margin
  let top = event.clientY + margin
  if (typeof window !== 'undefined') {
    if (left + maxW > window.innerWidth - margin)
      left = Math.max(margin, event.clientX - maxW - margin)
    if (top + maxH > window.innerHeight - margin)
      top = Math.max(margin, event.clientY - maxH - margin)
  }
  return { top: top + 'px', left: left + 'px' }
}
